import { useContext } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import classnames from 'classnames';

import Typography from '@classify/react-core-components/Typography';

import CourseContext from '../../contexts/courseContext';
import type { PartType } from '../../types';
import tocItemToUrl from '../../services/tocItemToUrl';

const useStyles = makeStyles(({ spacing, palette, typography }) => ({
  root: {
    listStyle: 'none',
    padding: spacing(1.5, 1, 1.5, 1),
    display: 'flex',
  },
  iconContainer: {
    paddingRight: spacing(2),
  },
  link: {
    textDecoration: 'none',
    color: palette.text.onMain,
    '&:hover': {
      color: palette.brand.main,
    },
  },
  boldLink: {
    fontWeight: typography.fontWeightBold,
  },
  isCurrentDisplayed: {
    background: palette.layout.background.alternative,
  },
  reseth4: {
    margin: 0,
    padding: 0,
    lineHeight: 'initial',
  },
}));

export interface TocItemProps {
  isCurrentDisplayed: boolean;
  learningStatusIcon: ReactNode;
  part: PartType;
  label: string;
  onClick?: () => void;
}

const TocItemWrapper = ({
  isCurrentDisplayed,
  learningStatusIcon,
  part,
  label,
  onClick = () => {},
}: TocItemProps) => {
  const classes = useStyles();

  const { course } = useContext(CourseContext);
  const { id, type, title } = part;

  const { pathname } = useLocation();
  const link = tocItemToUrl(course, { id, type, title });

  const linkProps = {
    component: Link,
    to: {
      pathname: link,
      search: window.location.search,
    },
    state: { from: pathname },
  };

  return (
    <li className={classnames(classes.root, { [classes.isCurrentDisplayed]: isCurrentDisplayed })}>
      <div className={classes.iconContainer}>{learningStatusIcon}</div>

      <h4 className={classes.reseth4}>
        <Typography
          className={classnames(classes.link, { [classes.boldLink]: isCurrentDisplayed })}
          variant="subheading"
          onClick={onClick}
          {...linkProps}
        >
          {label}
        </Typography>
      </h4>
    </li>
  );
};

export default TocItemWrapper;
